import { z } from "zod";
import type { ToolDefinition } from "../../mcp/registry";

export interface IndexHealthSnapshot {
  /** Boot reconcile lifecycle: `pending` until it settles, then `ok`, or `degraded` if any vault
   *  failed to reconcile. Non-identifying. */
  reconcile: "pending" | "ok" | "degraded";
  /** When the boot reconcile settled (ms epoch), or null while still pending. */
  reconcile_at: number | null;
  /** Count of index-on-write failures swallowed since boot (best-effort reindex/deindex). */
  write_failures: number;
  /** THE-291: the notes/FTS metadata pass completed (independent of embed success). */
  notes_ready?: boolean;
  /** Per-vault reconcile errors + last write error — authenticated callers only (may name paths). */
  detail?: {
    reconcile_errors: Array<{ vault: string; error: string }>;
    last_write_error?: string;
  };
}

export interface HealthInfo {
  status: "ok";
  name: "obsidian-tc";
  version: string;
  /** True when the compiled native search module is loaded (accelerated path). Non-identifying. */
  native_loaded: boolean;
  /** True when sqlite-vec loaded on the shared cache.db connection at boot. Non-identifying. */
  vec_enabled: boolean;
  /** True when FTS5 (notes_fts) is available on this connection (THE-291). Non-identifying. */
  fts_enabled: boolean;
  /** Number of configured vaults (always present, non-identifying). */
  vault_count: number;
  /** Vault id list — only for authenticated callers (ids are deployment-internal). */
  vaults?: string[];
  uptime_ms: number;
  /** Search-index health (THE-288). Always present; `detail` (per-vault reconcile errors + last
   *  write error) is authenticated-only since messages may name paths. */
  index?: IndexHealthSnapshot;
}

export function createHealthTool(opts: {
  version: string;
  vaults: string[];
  startedAt: number;
  nativeLoaded: boolean;
  vecEnabled: boolean;
  /** Optional so existing harnesses stay source-compatible; absent -> false. */
  ftsEnabled?: boolean;
  /** THE-288: returns a live index-health snapshot at call time, shaped by caller auth. */
  getIndexHealth?: (authenticated: boolean) => IndexHealthSnapshot;
}): ToolDefinition<Record<string, never>, HealthInfo> {
  return {
    name: "server_health",
    description:
      "Liveness + build info. Round-trips the full transport -> auth -> acl -> audit path.",
    inputSchema: z.object({}).strict(),
    requiredScopes: [],
    // requiredScopes [] keeps this an unauthenticated liveness probe, but the vault-id list is
    // emitted only to authenticated callers (F3): ids are deployment-internal. The native/vec
    // capability flags are non-identifying, so they stay in the always-present payload.
    handler: (_input, ctx) => ({
      status: "ok",
      name: "obsidian-tc",
      version: opts.version,
      native_loaded: opts.nativeLoaded,
      vec_enabled: opts.vecEnabled,
      fts_enabled: opts.ftsEnabled ?? false,
      vault_count: opts.vaults.length,
      ...(ctx.authenticated ? { vaults: opts.vaults } : {}),
      uptime_ms: Date.now() - opts.startedAt,
      ...(opts.getIndexHealth ? { index: opts.getIndexHealth(ctx.authenticated) } : {}),
    }),
  };
}
